import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { valueForOnlyNumericInputTextFields } from "utils/helpers";

import { SUSPENSION_TYPE_OPTIONS } from "../data";
import {
  selectChassisHeightForm,
  selectChassisHeightFormDropDowns,
  selectChassisHeightSaving,
  selectIsAddModalOpen,
} from "../redux/selector";
import { chassisHeightActions } from "../redux/slice";
import {
  CHASSIS_HEIGHT_FORM_FIELD_KINDS,
  ChassisHeightForm,
  ChassisHeightFormDropDownKey,
  ChassisHeightSelectOption,
} from "../types";

export type ChassisHeightFieldConfig = {
  key: keyof ChassisHeightForm;
  label: string;
  type: "select" | "text";
  options?: ChassisHeightSelectOption[];
  dropDownKey?: ChassisHeightFormDropDownKey;
};

export const sanitizeChassisHeightFormValue = (
  name: keyof ChassisHeightForm,
  value: string,
) => {
  if (CHASSIS_HEIGHT_FORM_FIELD_KINDS[name] === "number") {
    return valueForOnlyNumericInputTextFields(value);
  }
  return value;
};

const REQUIRED_FIELDS: Array<keyof ChassisHeightForm> = [
  "manufacturer",
  "masterModel",
  "suspensionType",
  "axleDistance",
  "tire",
  "year",
  "net",
  "chassisHeightIncludingBody",
];

const P = "CHASSIS_HEIGHT.MODAL";

export const useChassisHeightModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const open = useSelector(selectIsAddModalOpen);
  const form = useSelector(selectChassisHeightForm);
  const saving = useSelector(selectChassisHeightSaving);
  const { manufacturers, masterModels } = useSelector(
    selectChassisHeightFormDropDowns,
  );

  useEffect(() => {
    if (!open) return;
    dispatch(chassisHeightActions.getFormDropDownList("manufacturers"));
    dispatch(chassisHeightActions.getFormDropDownList("masterModels"));
  }, [open, dispatch]);

  const rows: ChassisHeightFieldConfig[][] = [
    [
      {
        key: "manufacturer",
        label: "MANUFACTURER",
        type: "select",
        dropDownKey: "manufacturers",
        options: manufacturers.items.map(({ id, name }) => ({
          value: String(id),
          label: name,
        })),
      },
      {
        key: "masterModel",
        label: "MASTER_MODEL",
        type: "select",
        dropDownKey: "masterModels",
        options: masterModels.items.map(({ id, name }) => ({
          value: String(id),
          label: name,
        })),
      },
    ],
    [
      {
        key: "suspensionType",
        label: "SUSPENSION_TYPE",
        type: "select",
        options: SUSPENSION_TYPE_OPTIONS,
      },
      { key: "axleDistance", label: "AXLE_DISTANCE", type: "text" },
    ],
    [
      { key: "tire", label: "TIRE", type: "text" },
      { key: "year", label: "YEAR", type: "text" },
    ],
    [
      { key: "net", label: "NET", type: "text" },
      {
        key: "chassisHeightIncludingBody",
        label: "CHASSIS_HEIGHT_INCLUDING_BODY",
        type: "text",
      },
    ],
  ];

  const onClose = () => {
    if (saving) return;
    dispatch(chassisHeightActions.closeAddModal());
  };

  const setField = (name: keyof ChassisHeightForm, value: string) =>
    dispatch(
      chassisHeightActions.setFormField({
        name,
        value: sanitizeChassisHeightFormValue(name, value),
      }),
    );

  const placeholder = (label: string, type: "select" | "text") =>
    t(`${P}.PLACEHOLDERS.${type === "select" ? "SELECT" : "ENTER"}_${label}`);

  const fieldLabel = (label: string) => t(`${P}.FIELDS.${label}`);

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  const optionLabel = (option: ChassisHeightSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const isNumberField = (key: keyof ChassisHeightForm) =>
    CHASSIS_HEIGHT_FORM_FIELD_KINDS[key] === "number";

  const onSubmit = () => dispatch(chassisHeightActions.addChassisHeight());

  const onDropDownScroll = (dropDownKey?: ChassisHeightFormDropDownKey) => {
    if (!dropDownKey) return;
    dispatch(chassisHeightActions.getFormDropDownNextPage(dropDownKey));
  };

  const hasMoreDropDownItems = (dropDownKey?: ChassisHeightFormDropDownKey) => {
    if (!dropDownKey) return false;
    const dropDown =
      dropDownKey === "manufacturers" ? manufacturers : masterModels;
    return (
      !dropDown.loading &&
      dropDown.meta.currentPage < dropDown.meta.totalPages
    );
  };

  return {
    t,
    open,
    form,
    saving,
    rows,
    isValid,
    addTitle: t(`${P}.ADD_TITLE`),
    onClose,
    setField,
    placeholder,
    fieldLabel,
    optionLabel,
    isNumberField,
    onSubmit,
    onDropDownScroll,
    hasMoreDropDownItems,
  };
};
